import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Button, 
  Space, 
  Typography, 
  Modal, 
  Form, 
  Input, 
  List, 
  Alert, 
  Tag, 
  Tooltip,
  Spin,
  message
} from 'antd'
import { 
  SaveOutlined, 
  FolderOpenOutlined, 
  CloudSyncOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WifiOutlined,
  DisconnectOutlined
} from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'
import { apiService, SaveDesignResponse } from '../../services/apiService'
import { isFeatureEnabled } from '../../config/environment'

const { Title, Text } = Typography
const { TextArea } = Input

const SaveLoadPanel: React.FC = () => {
  const { 
    currentDesignId, 
    isSaving, 
    lastSaved, 
    syncStatus,
    saveDesign, 
    loadDesign, 
    syncWithBackend,
    resetDesign,
    getAllObjects
  } = useDesignStore()

  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showLoadModal, setShowLoadModal] = useState(false)
  const [savedDesigns, setSavedDesigns] = useState<SaveDesignResponse[]>([])
  const [loadingDesigns, setLoadingDesigns] = useState(false)
  const [hasSavedDesigns, setHasSavedDesigns] = useState(false)
  const [saveForm] = Form.useForm()

  const allObjects = getAllObjects()
  const hasObjects = allObjects.length > 0
  const backendEnabled = isFeatureEnabled('backendSync')

  useEffect(() => {
    if (showLoadModal) {
      loadSavedDesigns()
    }
  }, [showLoadModal])

  // Check for saved designs on component mount
  useEffect(() => {
    const checkSavedDesigns = async () => {
      if (!backendEnabled) return
      
      try {
        const response = await apiService.listDesigns(1, 0) // Just check if any exist
        const designs = response.designs || (response as any).data || []
        setHasSavedDesigns(designs.length > 0)
      } catch (error) {
        console.error('Failed to check saved designs:', error)
        setHasSavedDesigns(false)
      }
    }
    
    checkSavedDesigns()
  }, [backendEnabled])

  const loadSavedDesigns = async () => {
    if (!backendEnabled) return

    setLoadingDesigns(true)
    try {
      const response = await apiService.listDesigns(20, 0)
      console.log('API Response:', response) // Debug log
      const designs = response.designs || (response as any).data || []
      setSavedDesigns(designs)
      setHasSavedDesigns(designs.length > 0)
    } catch (error) {
      message.error('Failed to load saved designs')
      console.error('Load designs error:', error)
      setSavedDesigns([]) // Ensure it's always an array
      setHasSavedDesigns(false)
    } finally {
      setLoadingDesigns(false)
    }
  }

  const handleSave = async (values: { name: string; description?: string }) => {
    try {
      const designId = await saveDesign(values.name, values.description)
      message.success('Design saved successfully!')
      setShowSaveModal(false)
      saveForm.resetFields()
      setHasSavedDesigns(true) // Update state since we just saved a design
    } catch (error) {
      message.error('Failed to save design')
      console.error('Save error:', error)
    }
  }

  const handleLoad = async (designId: string) => {
    try {
      await loadDesign(designId)
      message.success('Design loaded successfully!')
      setShowLoadModal(false)
    } catch (error) {
      message.error('Failed to load design')
      console.error('Load error:', error)
    }
  }

  const handleSync = async () => {
    try {
      await syncWithBackend()
      message.success('Synced with backend!')
    } catch (error) {
      message.error('Sync failed')
      console.error('Sync error:', error)
    }
  }

  const handleNewDesign = () => {
    Modal.confirm({
      title: 'Start New Design',
      content: 'This will clear your current design. Any unsaved changes will be lost.',
      okText: 'Start New',
      cancelText: 'Cancel',
      onOk: () => {
        resetDesign()
        message.success('Started new design')
      }
    })
  }

  const getSyncStatusIcon = () => {
    switch (syncStatus) {
      case 'syncing':
        return <Spin size="small" />
      case 'idle':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
      case 'offline':
        return <DisconnectOutlined style={{ color: '#faad14' }} />
      default:
        return <InfoCircleOutlined />
    }
  }

  const getSyncStatusText = () => {
    switch (syncStatus) {
      case 'syncing':
        return 'Syncing...'
      case 'idle':
        return 'Up to date'
      case 'error':
        return 'Sync error'
      case 'offline':
        return 'Offline'
      default:
        return 'Unknown'
    }
  }

  const formatLastSaved = (date: Date | null) => {
    if (!date) return 'Never'
    
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  if (!backendEnabled) {
    return (
      <Card title={
        <Space>
          <SaveOutlined />
          <Title level={4} style={{ margin: 0 }}>Save & Load</Title>
        </Space>
      }>
        <Alert
          message="Backend Sync Disabled"
          description="Save and load functionality requires backend integration. Designs are stored locally in your browser."
          type="info"
          showIcon
        />
      </Card>
    )
  }

  return (
    <>
      <Card
        title={
          <Space>
            <SaveOutlined />
            <Title level={4} style={{ margin: 0 }}>Save & Load</Title>
          </Space>
        }
        extra={
          <Space>
            {backendEnabled && (
              <Tooltip title={getSyncStatusText()}>
                <Button 
                  icon={getSyncStatusIcon()} 
                  size="small"
                  onClick={handleSync}
                  disabled={syncStatus === 'syncing'}
                />
              </Tooltip>
            )}
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* Current Design Status */}
          {currentDesignId && (
            <Alert
              message={
                <Space>
                  <Text strong>Current Design:</Text>
                  <Text code>{currentDesignId}</Text>
                </Space>
              }
              description={
                <Space>
                  <Text type="secondary">Last saved: {formatLastSaved(lastSaved)}</Text>
                  <Tag color={syncStatus === 'idle' ? 'green' : syncStatus === 'error' ? 'red' : 'blue'}>
                    {getSyncStatusText()}
                  </Tag>
                </Space>
              }
              type="info"
              showIcon
            />
          )}

          {/* Object Count */}
          <Space>
            <Text strong>Objects:</Text>
            <Tag color="blue">{allObjects.length} items</Tag>
            <Text type="secondary">
              ({allObjects.filter(o => o.type === 'ADU').length} ADUs, {allObjects.filter(o => o.type === 'Pool').length} Pools)
            </Text>
          </Space>

          {/* Action Buttons */}
          <Space wrap>
            <Tooltip 
              title={
                !hasObjects 
                  ? "Add some objects to your design first" 
                  : isSaving 
                    ? "Saving design..." 
                    : currentDesignId 
                      ? "Update the current design" 
                      : "Save your design to the cloud"
              }
            >
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                onClick={() => setShowSaveModal(true)}
                disabled={!hasObjects || isSaving}
                loading={isSaving}
              >
                {currentDesignId ? 'Update Design' : 'Save Design'}
              </Button>
            </Tooltip>
            
            <Tooltip 
              title={
                !backendEnabled 
                  ? "Backend sync is disabled" 
                  : !hasSavedDesigns 
                    ? "No saved designs available" 
                    : "Load a previously saved design"
              }
            >
              <Button 
                icon={<FolderOpenOutlined />}
                onClick={() => setShowLoadModal(true)}
                disabled={!backendEnabled || !hasSavedDesigns}
              >
                Load Design
              </Button>
            </Tooltip>
            
            <Button 
              icon={<EditOutlined />}
              onClick={handleNewDesign}
            >
              New Design
            </Button>
            
            {currentDesignId && (
              <Button 
                icon={<CloudSyncOutlined />}
                onClick={handleSync}
                disabled={syncStatus === 'syncing'}
              >
                Sync
              </Button>
            )}
          </Space>

          {/* Connection Status */}
          <Space>
            <WifiOutlined style={{ color: backendEnabled ? '#52c41a' : '#ff4d4f' }} />
            <Text type="secondary">
              {backendEnabled ? 'Connected to backend' : 'Backend unavailable'}
            </Text>
          </Space>
        </Space>
      </Card>

      {/* Save Modal */}
      <Modal
        title="Save Design"
        open={showSaveModal}
        onCancel={() => setShowSaveModal(false)}
        onOk={() => saveForm.submit()}
        confirmLoading={isSaving}
      >
        <Form
          form={saveForm}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            name: currentDesignId ? 'Updated Design' : 'My Design',
            description: ''
          }}
        >
          <Form.Item
            name="name"
            label="Design Name"
            rules={[{ required: true, message: 'Please enter a design name' }]}
          >
            <Input placeholder="Enter design name" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <TextArea 
              rows={3} 
              placeholder="Describe your design..."
            />
          </Form.Item>

          <Alert
            message={`Saving ${allObjects.length} objects`}
            description={`${allObjects.filter(o => o.type === 'ADU').length} ADUs and ${allObjects.filter(o => o.type === 'Pool').length} Pools`}
            type="info"
            showIcon
          />
        </Form>
      </Modal>

      {/* Load Modal */}
      <Modal
        title="Load Design"
        open={showLoadModal}
        onCancel={() => setShowLoadModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowLoadModal(false)}>
            Cancel
          </Button>
        ]}
        width={700}
      >
        <Spin spinning={loadingDesigns}>
          {!savedDesigns || savedDesigns.length === 0 ? (
            <Alert
              message="No Saved Designs"
              description="You haven't saved any designs yet. Create and save a design to see it here."
              type="info"
              showIcon
            />
          ) : (
            <List
              dataSource={savedDesigns || []}
              renderItem={(design) => (
                <List.Item
                  actions={[
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={() => handleLoad(design.id)}
                    >
                      Load
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        {(design as any).designData?.customizations?.name || 'Untitled Design'}
                        {design.id === currentDesignId && (
                          <Tag color="green">Current</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <Text type="secondary">{(design as any).designData?.customizations?.description || 'No description'}</Text>
                        <Space>
                          <Text type="secondary">
                            {(design as any).designData?.customizations?.objects?.length || 0} objects
                          </Text>
                          <Text type="secondary">•</Text>
                          <Text type="secondary">
                            Updated {new Date(design.updatedAt).toLocaleDateString()}
                          </Text>
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Modal>
    </>
  )
}

export default SaveLoadPanel
